'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const steps = [
  {
    number: 'i.',
    title: 'Call or Request Online',
    description: 'Share your appointment details — pickup address, destination, date, time, and any mobility equipment needed. Takes less than 3 minutes.',
    detail: 'Available by phone at (551) 326-9573 or via our online form below.',
    icon: 'PhoneIcon' as const,
    graphic: 'phone',
  },
  {
    number: 'ii.',
    title: 'Get a Transparent Quote',
    description: "We'll confirm coverage with your insurance carrier or give you a clear private-pay rate — no hidden fees, no surprises.",
    detail: 'Most insurance quotes confirmed within 1 business hour.',
    icon: 'DocumentTextIcon' as const,
    graphic: 'quote',
  },
  {
    number: 'iii.',
    title: 'We Arrive On Time',
    description: "Our driver arrives at your door with the right vehicle and equipment. Family members are welcome to ride along at no extra charge.",
    detail: 'GPS-tracked fleet. We call 30 minutes before arrival.',
    icon: 'TruckIcon' as const,
    graphic: 'arrive',
  },
];

type GraphicKey = 'phone' | 'quote' | 'arrive';

function StepGraphic({ graphic }: { graphic: GraphicKey }) {
  const graphicMap: Record<GraphicKey, React.ReactNode> = {
    phone: (
      <div className="flex flex-col items-center gap-4">
        <div className="w-20 h-20 rounded-full bg-accent/15 border-2 border-accent/30 flex items-center justify-center">
          <Icon name="PhoneIcon" size={36} className="text-accent" />
        </div>
        <div className="text-center">
          <p className="font-display text-2xl font-semibold text-primary">(551) 326-9573</p>
          <p className="text-sm text-muted-foreground mt-1">Available daily, 6 AM – 8 PM</p>
        </div>
        <div className="flex gap-3 mt-2">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
            <div key={d} className={`text-center ${i < 5 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-1 ${i < 5 ? 'bg-primary/10' : 'bg-muted'}`}>
                {d[0]}
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
    quote: (
      <div className="bg-muted rounded-2xl p-6 w-full max-w-xs border border-border">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Your Quote</span>
          <span className="text-xs text-sage font-semibold bg-sage/10 px-2 py-1 rounded-full">Confirmed</span>
        </div>
        {[
          { label: 'Transport Type', value: 'Wheelchair — One Way' },
          { label: 'Insurance', value: 'Horizon BCBS' },
          { label: 'Your Cost', value: '$0 copay' },
          { label: 'Pickup', value: 'Confirmed 8:15 AM' },
        ].map((row) => (
          <div key={row.label} className="flex justify-between py-2.5 border-b border-border last:border-0">
            <span className="text-xs text-muted-foreground">{row.label}</span>
            <span className="text-xs font-semibold text-foreground">{row.value}</span>
          </div>
        ))}
      </div>
    ),
    arrive: (
      <div className="flex flex-col items-center gap-4 w-full max-w-xs">
        <div className="w-full bg-muted rounded-2xl p-4 border border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
              <Icon name="TruckIcon" size={18} className="text-accent" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Driver arriving in</p>
              <p className="font-display text-2xl font-bold text-primary">28 min</p>
            </div>
          </div>
          <div className="w-full bg-border rounded-full h-2">
            <div className="bg-accent h-2 rounded-full" style={{ width: '65%' }} />
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="UserGroupIcon" size={16} className="text-sage" />
          <span>Family members may ride along</span>
        </div>
      </div>
    ),
  };

  return (
    <div className="flex items-center justify-center h-full p-8">
      {graphicMap[graphic]}
    </div>
  );
}

export default function HowItWorksSection() {
  const [activeStep, setActiveStep] = useState(0);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers = stepRefs.current.map((ref, idx) => {
      if (!ref) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveStep(idx);
        },
        { rootMargin: '-40% 0% -40% 0%', threshold: 0 }
      );
      obs.observe(ref);
      return obs;
    });
    return () => observers.forEach((obs) => obs?.disconnect());
  }, []);

  return (
    <section
      className="py-20 bg-background"
      aria-labelledby="how-it-works-heading"
    >
      <div className="max-w-screen-xl mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex flex-col items-center">
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">How It Works</span>
            <div className="w-px h-10 bg-border mb-3" aria-hidden="true" />
          </div>
          <h2
            id="how-it-works-heading"
            className="text-section-xl font-display font-light text-primary"
          >
            Transport in{' '}
            <span className="italic text-muted-foreground">3 simple steps</span>
          </h2>
        </div>

        {/* Sticky scroll layout */}
        <div className="relative flex flex-col lg:flex-row gap-12 items-start">

          {/* Steps — Left */}
          <div className="w-full lg:w-1/2 space-y-16 py-8 lg:py-20">
            {steps.map((step, idx) => (
              <div
                key={step.title}
                ref={(el) => { stepRefs.current[idx] = el; }}
                className={`step-content transition-opacity duration-500 ${activeStep === idx ? 'active' : ''}`}
              >
                <span className="text-sm font-medium text-muted-foreground mb-4 block">{step.number}</span>
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${
                    activeStep === idx ? 'bg-accent' : 'bg-muted border border-border'
                  }`}>
                    <Icon
                      name={step.icon}
                      size={18}
                      className={activeStep === idx ? 'text-accent-foreground' : 'text-muted-foreground'}
                    />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-medium text-primary mt-1">
                    {step.title}
                  </h3>
                </div>
                <p className="text-base text-muted-foreground leading-relaxed max-w-md mb-3">
                  {step.description}
                </p>
                <p className="text-sm text-muted-foreground/70 italic">{step.detail}</p>
              </div>
            ))}

            {/* Final CTA */}
            <div className="pt-4">
              <a href="#request-transport" className="btn-primary inline-flex">
                <Icon name="ClipboardDocumentListIcon" size={16} />
                Request Transport Now
              </a>
            </div>
          </div>

          {/* Graphic — Right Sticky */}
          <div className="hidden lg:block w-1/2 lg:sticky lg:top-24 h-[60vh]">
            <div className="w-full h-full bg-muted rounded-2xl border border-border flex items-center justify-center overflow-hidden transition-all duration-700">
              <StepGraphic graphic={steps[activeStep].graphic as GraphicKey} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}